import axios from "axios";
import { errorCodes, type ErrorCode } from "@opendialysis/procare-api-schemas";

/**
 * The API answers 4xx with `{ message, code? }` and 5xx with
 * `{ message: "Internal Server Error" }`. `code` is the stable, machine-readable
 * part and the only thing worth branching on — messages are English and change.
 *
 * Three shapes carry NO code and must still be survivable: the X-Api-Key gate
 * (`{message:"Unauthorized"}`), the 404 handler (`{message:"Not Found"}`) and
 * Fastify/zod validation failures.
 */
export type NormalisedError = {
  status: number;
  code?: ErrorCode;
  /** Server-provided English text. For logs and debugging, not for the UI. */
  message: string;
  isNetwork: boolean;
};

function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && value in errorCodes;
}

export function normaliseError(error: unknown): NormalisedError {
  // axios.isAxiosError checks the `isAxiosError` flag rather than the
  // prototype. `instanceof AxiosError` silently fails whenever two copies of
  // axios end up in the graph — a live hazard in a hoisted workspace — and
  // every API error would then degrade to "unexpected error".
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return {
        status: 0,
        message: error.message || "Network error",
        isNetwork: true,
      };
    }
    const body = error.response.data as
      | { message?: unknown; code?: unknown }
      | undefined;
    return {
      status: error.response.status,
      code: isErrorCode(body?.code) ? body.code : undefined,
      message:
        typeof body?.message === "string" ? body.message : error.message,
      isNetwork: false,
    };
  }

  if (error instanceof Error) {
    return { status: 0, message: error.message, isNetwork: false };
  }
  return { status: 0, message: String(error), isNetwork: false };
}

const KNOWN_STATUSES = [400, 401, 403, 404, 409, 429, 500] as const;
type KnownStatus = (typeof KNOWN_STATUSES)[number];

/**
 * Keys this module can produce, as a literal union rather than `string`, so
 * `t()` keeps checking them against the catalogue. A key added here without a
 * translation is a type error; a code added on the API without a translation
 * is caught by catalogues.test.ts.
 */
export type ErrorMessageKey =
  | `errors:${ErrorCode}`
  | `errors:HTTP_${KnownStatus}`
  | "errors:NETWORK"
  | "errors:UNKNOWN";

/**
 * Translation key for an error, in the `errors` namespace. Resolution order:
 * the stable code, then a network fallback, then the status bucket, then
 * UNKNOWN — so a clinician never sees a raw enum name or an English server
 * message.
 */
export function errorMessageKey(error: NormalisedError): ErrorMessageKey {
  if (error.code) return `errors:${error.code}`;
  if (error.isNetwork) return "errors:NETWORK";
  const known = KNOWN_STATUSES.find((status) => status === error.status);
  if (known) return `errors:HTTP_${known}`;
  if (error.status >= 500) return "errors:HTTP_500";
  return "errors:UNKNOWN";
}

/** Convenience for call sites that only have the raw thrown value. */
export function toMessageKey(error: unknown): ErrorMessageKey {
  return errorMessageKey(normaliseError(error));
}
